import { PAGE_SUB, PAGES } from 'context/pageContext';

export const formatCurrency = (
  amount: number | string | undefined | null,
  currency: string
): string => {
  if (!amount) return 'N/A';
  if (typeof amount === 'string') {
    amount = parseFloat(amount);
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

export const debounce = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

/**
 * Why the payment failed, as the API put it in a response body, e.g.
 * { response: "FAILED", metadata: { message: "Transaction declined." } }.
 */
export const getResponseMessage = (body: any): string | undefined => {
  const message = body?.metadata?.message || body?.message;
  return typeof message === 'string' && message ? message : undefined;
};

/**
 * A body that reports a failure while the request itself succeeded, e.g.
 * HTTP 200 with { statusCode: 400, message: "Incorrect PIN" }.
 */
export const isErrorBody = (body: any): boolean =>
  Number(body?.statusCode) >= 400;

export const checkResponse = (
  response: string,
  updateSub: (sub: PAGE_SUB, message?: string) => void,
  updatePage: (sub: PAGES) => void,
  // shown on the error screen when the response is a failure
  message?: string
) => {
  switch (response) {
    case 'SEND_PIN':
      updateSub(PAGE_SUB.PIN_AUTH);
      return;
    case 'SEND_OTP':
    case '01':
      updateSub(PAGE_SUB.OTP_AUTH);
      return;
    case 'SEND_BIRTHDAY':
      updateSub(PAGE_SUB.BIRTHDAY_AUTH);
      return;
    case 'SEND_PHONE':
      updateSub(PAGE_SUB.PHONE_AUTH);
      return;
    case 'APPROVED':
    case '00':
      updatePage(PAGES.AUTHENTICATING);
      updateSub(PAGE_SUB.SUCCESS);
      return;
    case 'PENDING':
      updateSub(PAGE_SUB.PAYING);
      return;
    case 'TIMED_OUT':
      updateSub(PAGE_SUB.TIMEOUT);
      return;
    case 'OPEN_URL':
    case '02':
    case '03':
    case '04':
      updateSub(PAGE_SUB.BANK_AUTH);
      return
    case 'UNKNOWN':
      updateSub(PAGE_SUB.ERROR, message);
      return;
    case 'FAILED':
    case 'DECLINED':
      updateSub(PAGE_SUB.ERROR, message);
      return;
    case 'REFUNDED':
      updateSub(PAGE_SUB.REFUNDED);
      return;
    case 'REFUND_IN_PROGRESS':
      updateSub(PAGE_SUB.REFUND_IN_PROGRESS);
      return;
    default:
      console.log('Unknown status!!');
      return;
  }
};

export const getTransferStatus = (
  status: string,
  updateSub: (sub: string) => void,
  updatePage: (sub: string) => void
) => {
  switch (status) {
    case 'APPROVED':
      updatePage('Authenticating');
      updateSub('Success');
      return;
    case 'DECLINED':
      updateSub('Declined');
      return;
    case 'FAILED':
      updateSub('Failed');
      return;
    case 'IN_PROGRESS':
      updateSub('Paying');
      return;
    case 'CANCELLED':
      updateSub('Cancelled');
      return;
    case 'INITIALISED':
      return;
    case 'SETTLED':
      updatePage('Authenticating');
      updateSub('Success');
      return;
    case 'PENDING':
      updateSub('Paying');
      return;
    case 'TIMED_OUT':
      updateSub('Timeout');
      return;
    case 'UNKNOWN':
      updateSub('Error');
      return;
    case 'REFUNDED':
      updateSub('Refunded');
      return;
    case 'REFUND_IN_PROGRESS':
      updateSub('RefundInProgress');
      return;
    default:
      console.log('Unknown status!!');
      return;
  }
};
